import { COUNTRY_CODE, CURRENT_LNG, PATH_AFTER_PAYMENT_RESULT, USER_PAYMENT_SELECTION } from 'constants/LocalStorage';

/**
 * Get current UI language code from localStorage.
 * @returns {string|null}
 */
export const getCurrentLngFromLocalStorage = () => {
  return localStorage.getItem(CURRENT_LNG);
};

/**
 * Get current country code from localStorage.
 * @returns {string|null}
 */
export const getCountryCodeFromLocalStorage = () => {
  return localStorage.getItem(COUNTRY_CODE);
};

/**
 * Persist country code in localStorage.
 * @param {string} newValue
 * @returns {void}
 */
export const setCountryCodeInLocalStorage = (newValue) => {
  localStorage.setItem(COUNTRY_CODE, newValue);
};

/**
 * Persist user's payment selection in localStorage.
 * @param {string} newProvider - Payment provider identifier.
 * @param {Method} newMethod - Payment method identifier.
 * @returns {void}
 */
export const setUserPaymentSelectionInLocalStorage = (newProvider, newMethod) => {
  const newValue = JSON.stringify({
    provider: newProvider,
    method: newMethod,
  });
  localStorage.setItem(USER_PAYMENT_SELECTION, newValue);
};

/**
 * User's preferred payment selection persisted in localStorage.
 * @typedef {Object} UserPaymentSelection
 * @property {string} provider - Payment provider identifier (e.g. 'sebEveryPay').
 * @property {Method} method - Payment method identifier within the provider.
 */

/**
 * UserPaymentSelection method object.
 * @typedef {Object} Method
 * @property {string} id - Identifier used in the SEB EveryPay API (e.g. 'card', 'seb_ob_lt', 'google_pay', etc.).
 * @property {string} name - Display name of the payment methods.
 */

/**
 * Retrieve user's payment selection from localStorage.
 * Performs a one-time migration from legacy key 'userSelectedPaymentProvider'.
 * @returns {UserPaymentSelection|null}
 */
export const getUserPaymentSelectionFromLocalStorage = () => {
  // One-time migration from the old string parameter 'userSelectedPaymentProvider'
  // to the new object parameter 'userPaymentSelection'.
  const oldKey = 'userSelectedPaymentProvider';
  const oldValue = localStorage.getItem(oldKey);
  if (oldValue) {
    setUserPaymentSelectionInLocalStorage(oldValue);
    localStorage.removeItem(oldKey);
  }

  const value = localStorage.getItem(USER_PAYMENT_SELECTION);
  if (!value) return null;
  return JSON.parse(value);
};

/**
 * Remove a user's payment selection from localStorage.
 * @returns {void}
 */
export const removeUserPaymentSelectionFromLocalStorage = () => {
  localStorage.removeItem(USER_PAYMENT_SELECTION);
};

/**
 * Get a path after payment result from localStorage.
 * @returns {string|null}
 */
export const getPathAfterPaymentResultFromLocalStorage = () => {
  return localStorage.getItem(PATH_AFTER_PAYMENT_RESULT);
}

/**
 * Set a path after payment result from localStorage.
 * 
 * @returns {void}
 */
export const setPathAfterPaymentResultInLocalStorage = (newValue) => {
  localStorage.setItem(PATH_AFTER_PAYMENT_RESULT, newValue);
}

/**
 * Remove a path after payment result from localStorage.
 * @returns {void}
 */
export const removePathAfterPaymentResultFromLocalStorage = () => {
  localStorage.removeItem(PATH_AFTER_PAYMENT_RESULT);
}